import React, { useEffect, useMemo } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { Input, TextArea, Radio } from "@components/form";
import { post, put } from "@utils/axios_api";
import { validateDuplicateBizRegNo } from "@utils/validation";
import { useQueryClient } from '@tanstack/react-query';

function CompanyForm({ selectedData, onSaved, onAdd }) {
    const defaultFormValues = useMemo(() => ({
      bizRegNo: "",
      oldBizRegNo: "",
      companyNm: "",
      representNm: "",
      phoneNo: "",       // ex) 02-123-4567
      faxNo: "",         // ex) 02-123-4567
      compMgrPhone: "",  // ex) 010-1234-5678
      taxPhone: "",      // ex) 02-123-4567
      zipcode: "",
      address1: "",
      address2: "",
      bizTypeNm: "",
      bizItemNm: "",
      email: "",
      employeeCnt: "",
      eduEmpCnt: "",
      compMgrNm: "",
      compMgrEmail: "",
      taxMgrNm: "",
      taxMgrEmail: "",
      closeYn: "N",
      description: "",
    }),[]);

  const methods = useForm({
    defaultValues: defaultFormValues, // defaultValues : react-hook-form 초기값 설정 옵션
  });

  const { reset, setValue, handleSubmit, control } = methods;
  const isEdit = useMemo(() => !!selectedData, [selectedData]); // 수정 모드, 등록 모드 판별
  const queryClient = useQueryClient();


  // 목록에서 선택한 값 세팅
  useEffect(() => {
    if (isEdit) {
      reset({
        ...selectedData,
        oldBizRegNo: selectedData.bizRegNo  
      });
    } else {
      reset(defaultFormValues);
    }
  }, [isEdit, selectedData, reset, defaultFormValues]);

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        setValue("zipcode", data.zonecode, { shouldValidate: true });
        setValue("address1", data.address, { shouldValidate: true });
      },
    }).open();
  };

  const handleAdd = () => {
    reset(defaultFormValues);
    onAdd && onAdd();
  };

  const onSubmit = async (formData) => {
    try {
      let response;
        if (isEdit) {
            response = await put("/admin/comp/updateCompany", formData);
        } else {
            response = await post("/admin/comp/insertCompany", formData);
        }

        alert(response?.message || "저장 성공");
        await queryClient.invalidateQueries({ queryKey: ["selectAdminCompanyList"] });

        // onSaved && onSaved();
    } catch (err) {
      console.error(err);
      alert("등록/수정 실패");
    }
  };

  const onError = () => {
    alert("필수 입력란을 확인하세요.");
  }

  // ============ 수정: 자동 하이픈 포맷 함수 추가 ============ //
  const formatBizRegNo = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 15);
    const part1 = digits.substring(0, 3);
    const part2 = digits.substring(3, 5);
    const part3 = digits.substring(5);
    if (!part2) return part1;
    if (!part3) return `${part1}-${part2}`;
    return `${part1}-${part2}-${part3}`;
  };
  // ======================================================== //

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)} >
        <div className="section-tit-wrap">
            <h3 className="border-left-primary">&nbsp;업체 등록/수정</h3>
            <div className="btn-right-box">
            <ul>
                <li><button type="button" title="추가" className="btn-add" onClick={handleAdd}>추가</button></li>
                <li><button type="submit" title="저장" className="btn-save">저장</button></li>
            </ul>
            </div>
        </div>

        <table className="table-write">
            <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "40%" }} />
            </colgroup>
            <tbody>
            <tr>
                <th>사업장관리번호<span className="red">*</span></th>
                <td>
                <Controller
                  name="bizRegNo"
                  control={control}
   
                  rules={{
                    required: "사업자번호를 입력하세요.",
                    pattern: {
                      value: /^[0-9\-]+$/,
                      message: "숫자와 '-'만 입력 가능합니다.",
                    },
                    validate: (value) => {
                      if (selectedData && value === selectedData.bizRegNo) {
                        return true;
                      }
                      return validateDuplicateBizRegNo(value);
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="사업장관리번호"
                      className="input-text"
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        const formatted = formatBizRegNo(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />
                </td>
                <th>사업장명<span className="red">*</span></th>
                <td>
                <Input
                    name="companyNm"
                    placeholder="사업장명"
                    className="input-text"
                    style={{ width: "100%" }}
                    rules={{ required:true }}
                    />
                </td>
            </tr>
            <tr>
                <th>대표자명<span className="red">*</span></th>
                <td>
                <Input
                    name="representNm"
                    placeholder="대표자명"
                    className="input-text"
                    style={{ width: "100%" }}
                    rules={{ required:true }}
                    />
                </td>
                <th>이전사업자등록번호</th>
              <td>
              <Input
                    name="oldBizRegNo"
                    placeholder="(선택)"
                    className="input-text"
                    style={{ width: "100%" }}
                    />
              </td>
            </tr>
            <tr>
                <th>업태</th>
                <td >
                    <Input
                        name="bizTypeNm"
                        placeholder="업태"
                        className="input-text"
                        style={{ width: "100%" }}
                    />
                </td>
                <th>종목</th>
                <td >
                    <Input
                        name="bizItemNm"
                        placeholder="종목"
                        className="input-text"
                        style={{ width: "100%" }}
                    />
                </td>
            </tr>
            <tr>
            <th>
                전화번호
              </th>
              <td>
                <Input
                  name="phoneNo"
                  placeholder="ex) 02-123-4567"
                  className="input-text"
                  style={{ width: "100%" }}
                  rules={{
                    validate: (v) =>
                      !v || v === "" || /^\d{2,3}-\d{3,4}-\d{4}$/.test(v) ||
                      "예) 02-1234-5678 형식으로 입력",
                  }}
                />
              </td>
              <th>
                FAX번호
              </th>
              <td>
                <Input
                  name="faxNo"
                  placeholder="ex) 02-123-4567"
                  className="input-text"
                  style={{ width: "100%" }}
                  rules={{
                    validate: (v) =>
                      !v || v === "" || /^\d{2,3}-\d{3,4}-\d{4}$/.test(v) ||
                      "예) 02-1234-5678 형식으로 입력",
                  }}
                />
              </td>
            </tr>
            <tr>
                <th>직원수</th>
                <td >
                    <Input
                        name="employeeCnt"
                        placeholder="직원수"
                        className="input-text"
                        style={{ width: "100%" }}
                        rules={{
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "숫자만 입력 가능합니다."
                          }
                        }}
                    />
                </td>
                <th>교육인원수</th>
                <td >
                    <Input
                        name="eduEmpCnt"
                        placeholder="교육인원수"
                        className="input-text"
                        style={{ width: "100%" }}
                        rules={{
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "숫자만 입력 가능합니다."
                          }
                        }}
                    />
                </td>
            </tr>
            <tr>
            <th>교육담당자</th>
                <td >
                    <Input
                        name="compMgrNm"
                        placeholder="교육담당자"
                        className="input-text"
                        style={{ width: "100%" }}
                    />
                </td>
                <th>교육담당자<br/>전화번호</th>
                <td>
                <Input
                  name="compMgrPhone"
                  placeholder="ex) 010-1234-5678"
                  className="input-text"
                  style={{ width: "100%" }}
                  rules={{
                    validate: (v) =>
                      !v || v === "" || /^\d{2,3}-\d{3,4}-\d{4}$/.test(v) ||
                      "예) 02-1234-5678 형식으로 입력",
                  }}
                />
              </td>
            </tr>
            <tr>
              <th>교육담당자<br/>(이메일)</th>
                <td >
                    <Input
                        name="compMgrEmail"
                        placeholder="교육 담당자 (이메일)"
                        className="input-text"
                        style={{ width: "100%" }}
                        rules={{
                          pattern: {
                              value: /^[A-Za-z0-9@.]+$/,
                              message: "이메일 형태로 작성해주세요"
                            }
                        }}
                    />
                </td>
                <th>세금계산서<br/>담당자</th>
                <td >
                    <Input
                        name="taxMgrNm"
                        placeholder="세금계산서 담당자"
                        className="input-text"
                        style={{ width: "100%" }}
                    />
                </td>
            </tr>
            <tr>
            <th>세금계산서<br/>담당자전화번호</th>
            <td>
                <Input
                  name="taxPhone"
                  placeholder="ex) 02-123-4567"
                  className="input-text"
                  style={{ width: "100%" }}
                  rules={{
                    validate: (v) =>
                      !v || v === "" || /^\d{2,3}-\d{3,4}-\d{4}$/.test(v) ||
                      "예) 02-1234-5678 형식으로 입력",
                  }}
                />
              </td>
                <th>세금계산서<br/>담당자(이메일)</th>
                <td >
                    <Input
                        name="taxMgrEmail"
                        placeholder="세금계산서 담당자 (이메일)"
                        className="input-text"
                        style={{ width: "100%" }}
                        rules={{
                          pattern: {
                              value: /^[A-Za-z0-9@.]+$/,
                              message: "이메일 형태로 작성해주세요"
                            }
                        }}
                    />
                </td>

            </tr>
            <tr>
                <th>주소</th>
                <td colSpan={3}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: '8px', display:'flex' }}>
                      <Input
                        name="zipcode"
                        placeholder="우편번호"
                        readOnly
                        className="input-text"
                        style={{width:"100%"}}
                      />
                      <button
                        type="button"
                        className="btn-add btn-form-search"
                        onClick={handleAddressSearch}
                        style={{marginLeft:"10px"}} >
                        검색
                      </button>
                    </div>
                    <div>
                      <Input
                        name="address1"
                        placeholder="주소"
                        readOnly
                        className="input-text"
                        maxLength={30}
                        style={{width:"100%", marginBottom:"10px"}}
                      />
                      <Input
                        name="address2"
                        placeholder="상세주소를 입력하세요"
                        className="input-text"
                        maxLength={30}
                        style={{width:"100%"}}
                      />
                    </div>
                  </div>
                </td>
            </tr>
            <tr>
                <th>비고</th>
                <td colSpan="3">
                    <TextArea
                        name="description"
                        className="area"
                        autoResize
                        placeholder="비고"
                    />
                </td>
            </tr>
            <tr>
            <th>폐업여부</th>
            <td colSpan={3}>
              <div style={{ display: "flex", gap: "15px" }}>
                <Radio
                  name="closeYn"
                  label="정상"
                  value="N"

                />
                <Radio
                  name="closeYn"
                  label="폐업"
                  value="Y"
                />
              </div>
            </td>
            </tr>
            </tbody>
        </table>
      </form>
    </FormProvider>
  );
}

export default CompanyForm;
